import { Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, output, signal, SimpleChanges, ViewChild} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { AuthService } from '../../auth/auth.service';
import { MessageCenterService } from '../../services/messageCenter.service';
import { ToastService } from '../../services/toast.service';
import { SystemUserMessageResponse } from '../../models/RequestQuote';
import { Subscription } from 'rxjs';
import { GetUserDetails } from '../../models/sessionStorage.model';
import { ToastType } from '../../enums/config.enum';
import { BrokerQuoteService } from '../../services/brokerQuote.service';
import { UserNameList } from '../../models/QuoteList';

@Component({
  selector: 'app-message-center',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './message-center.component.html',
  styleUrl: './message-center.component.scss',
  providers: [DatePipe]
})
export class MessageCenter implements OnInit, OnDestroy{
    @ViewChild('messageCenterContainer') private messageCenterContainer!: ElementRef;
    userMessageEmitter = output<string>();
    datePipe = inject(DatePipe);
    authService = inject(AuthService);
    messageCenterService = inject(MessageCenterService);
    brokerQuoteService = inject(BrokerQuoteService);
    toastService = inject(ToastService);
    newMessage = signal<string>('');
    messageSubs = new Subscription();
    userNameList = signal<UserNameList[]>([]);
    messages = signal([
      { text: 'Please complete Clearance data and Enter Target Premium', 
        userName: 'Alchemy', time: this.messageCenterService.getCurrentDateTime(), isAdmin: true, isInitial: true, isSystemMessage: true
      },
    ]);
    storageData = signal<GetUserDetails | null>(null);

    ngOnInit(): void {
      const storageData = this.authService.getUserDetailsFromStorage();
      this.storageData.set(storageData);
      this.onSystemUserMessageHandler();
      // dynamically fetch the system user messages
      this.messageSubs = this.messageCenterService.getSystemUserMessages().subscribe({
        next: (action: boolean) => {
          if(action){
            this.onSystemUserMessageHandler();
          }
        }
      });
    }

    private scrollToBottom(): void {
        setTimeout(() => {
          if (this.messageCenterContainer) {
            const container = this.messageCenterContainer.nativeElement;
            container.scrollTop = container.scrollHeight;
          }
        }, 0);
    }

    onSystemUserMessageHandler(){
      if(this.isIndicationType()){ // indication type
        if(this.authService?.getUserDetailsFromStorage()?.indicationId){ // indication messages
          const indicationId = this.authService?.getUserDetailsFromStorage()?.indicationId;
          this.fetchIndicationMessage(String(indicationId));
        }else{ // clearance message with tempQuoteId
          const tempQuoteId = sessionStorage.getItem('tempQuoteId');
          this.fetchClearanceMessage(String(tempQuoteId));
        }
      }else{ //quote type
        if(this.authService.isPolicyType()){
          this.fetchPolicyMessage(String(this.authService.getUserDetailsFromStorage()?.policyId));
        }else{
          const quoteId = this.authService?.getUserDetailsFromStorage().quoteId;
          this.fetchQuoteMessage(String(quoteId));
        }
      }
    }

    fetchClearanceMessage(tempQuoteId: string){
      this.messageCenterService.getSystemUserMessagesClearance(tempQuoteId).subscribe({
        next: (response: SystemUserMessageResponse[]) => {
          if(response && response.length > 0){
            this.onBindMessageCenterData(response);
            this.messageCenterService.resetSystemUserMessages();
          }
        },
        error: (error: any) => {
        }
      });
    }

    fetchIndicationMessage(indicationId: string) {
      this.messageCenterService.getSystemUserMessagesIndication(indicationId).subscribe({
        next: (response: SystemUserMessageResponse[]) => {
          if(response && response.length > 0){
            this.onBindMessageCenterData(response);
            this.messageCenterService.resetSystemUserMessages();
          }
        },
        error: (error: any) => {
        }
      });
    }

    fetchQuoteMessage(quoteId: string) {
      this.messageCenterService.getSystemUserMessagesQuote(quoteId).subscribe({
        next: (response: SystemUserMessageResponse[]) => {
          if(response && response.length > 0){
            this.onBindMessageCenterData(response);
            this.messageCenterService.resetSystemUserMessages();
          }
        },
        error: (error: any) => {
        }
      });
    }

    fetchPolicyMessage(policyId: string) {
      this.messageCenterService.getSystemUserMessagesPolicy(policyId).subscribe({
        next: (response: SystemUserMessageResponse[]) => {
          if(response && response.length > 0){
            this.onBindMessageCenterData(response);
            this.messageCenterService.resetSystemUserMessages();
          }
        },
        error: (error: any) => {
        }
      });
    }

    onNewMessageByUserHandler(userMessage: string){
      const userId = this.storageData()?.userDetails?.userId;
      if(this.isIndicationType()){ // indication type
        if(this.authService?.getUserDetailsFromStorage()?.indicationId){ // post new message with indicationId
          const indicationId = this.authService?.getUserDetailsFromStorage()?.indicationId;
          this.onIndicationNewMessage(String(userId), String(indicationId), userMessage);
        }else{ // post new clearance message with tempQuoteId
          const tempQuoteId = sessionStorage.getItem('tempQuoteId');
          this.onClearanceNewMessage(String(userId), String(tempQuoteId), userMessage);
        }
      }else{ //policy type
        if(this.authService.isPolicyType()){
          this.onPolicyNewMessage(String(userId), String(this.authService.getUserDetailsFromStorage()?.policyId), userMessage);
        }else{ //quote type
          const quoteId = this.authService?.getUserDetailsFromStorage().quoteId;
          this.onQuoteNewMessage(String(userId), String(quoteId), userMessage);
        }
      }
    }

    onClearanceNewMessage(userId: string, tempQuoteId: string, userMessage: string) {
      this.messageCenterService.newUserMessageClearance(userId, tempQuoteId, userMessage).subscribe({
        next: (response) => {
          if(response.success){
            this.onSystemUserMessageHandler();
          }
        },error: (error: any) => {
          this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
        }
      });
    }

    onIndicationNewMessage(userId: string, indicationId: string, userMessage: string) {
      this.messageCenterService.newUserMessageIndication(userId, indicationId, userMessage).subscribe({
        next: (response) => {
          if(response.success){
            this.onSystemUserMessageHandler();
          }
        },error: (error: any) => {
          this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
        }
      });
    }

    onQuoteNewMessage(userId: string, quoteId: string, userMessage: string) {
      this.messageCenterService.newUserMessageQuote(userId, quoteId, userMessage).subscribe({
        next: (response) => {
          if(response.success){
            this.onSystemUserMessageHandler();
          }
        },error: (error: any) => {
          this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
        }
      });
    }

    onPolicyNewMessage(userId: string, policyId: string, userMessage: string){
      this.messageCenterService.newUserMessagePolicy(userId, policyId, userMessage).subscribe({
        next: (response) => {
          if(response.success){
            this.onSystemUserMessageHandler();
          }
        },error: (error: any) => {
          this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
        }
      });
    }

    getUserName(data:UserNameList[], userId: number) {
      const getName = data.filter((item) => item.userId === userId);
      if(getName.length > 0){
        return getName[0]?.fullName;
      }
      return '';
    }

    checkBrokerUserByRoleId(data:UserNameList[], userId: number): boolean{
      const getUser = data.filter((item) => item.userId === userId);
      if(getUser.length > 0) {
        return getUser[0]?.userRoleId === 1;
      }
      return false;
    }

    checkUWAdminUserByRoleId(data:UserNameList[], userId: number): boolean{
      const getUser = data.filter((item) => item.userId === userId);
      if(getUser.length > 0) {
        return getUser[0]?.userRoleId === 2;
      }
      return false;
    }

    onBindMessageCenterData(messagesData: SystemUserMessageResponse[]){
      const getPreviousMessage: any[] = [];
      if(this.isBroker()){ // display message for broker user
        messagesData.forEach((message: SystemUserMessageResponse) => {
          getPreviousMessage.push({
            time: message.messageDate,
            text: message.message,
            userName: (message?.mappingId ) ? "Alchemy" : message?.userName,
            isAdmin: (message?.mappingId || message?.roleId === 2) ? true : false,
            isInitial: false,
            isSystemMessage: message?.mappingId ? true : false
          });
        })
      }else{// display message for UW Admin
        messagesData.forEach((message: SystemUserMessageResponse) => {
          getPreviousMessage.push({
            time: message.messageDate,
            text: message.message,
            userName: (message?.mappingId ) ? "Alchemy" : message?.userName,
            isAdmin: (message?.mappingId === null && message?.roleId === 1) ? true : false,
            isInitial: false,
            isSystemMessage: message?.mappingId ? true : false
          });
        })
      }
      this.messages.set(getPreviousMessage);
      this.scrollToBottom();
    }

    onNewMessage(event: Event): void {
      const input = event.target as HTMLInputElement;
      if(input.value){
        this.newMessage.set(input.value);
      }
    }

    onEnterKey(event: Event): void {
      if (this.newMessage().trim()) {
        // If message is valid, send it
        this.sendMessage(this.newMessage());
        event.preventDefault(); // Prevent newline in the textarea
      } else {
        // Prevent Enter key action if input is empty
        event.preventDefault();
      }
    }

    sendMessage(userMessage: string) {
        if (userMessage.trim() !== '') {
          this.newMessage.set('');
          this.onNewMessageByUserHandler(userMessage); // New Message Handler
          // this.userMessageEmitter.emit(userMessage);
        }
    }

    convertDateTime(dateTimeString: string) {
        const date = new Date(dateTimeString);
        if(date){
          const formattedData = this.datePipe.transform(date, 'MM/dd/yy HH:mm');
          return formattedData;
        }
        return;
    }

    isIndicationType(): boolean {
      const quoteType = this.authService.getUserDetailsFromStorage()?.quoteType;
      return quoteType === "indication";
    }

    ngOnDestroy(): void {
      this.messageSubs.unsubscribe();
    }

    isUwAdmin(): boolean {
      return Number(this.storageData()?.userRole) === 2;
    }

    isBroker(): boolean {
      return Number(this.storageData()?.userRole) === 1;
    }
  
}